<template>
<div class="sheet-add-field">
  <div v-if="selected" class="row">
    <div class="col-xs-4">
      <div :class="indentationLevel">
        <h4 v-html="parameters.context.title"></h4>
      </div>
    </div>
    <div class="col-xs-1 text-right">
      <h4 class="text-primary glyphicon glyphicon-question-sign"
          @mouseover="showHint"
          @mouseleave="hideHint"
          aria-hidden="true"
          :id="hintUid"></h4>
      <tooltip :text="hintTooltip"
               :target="`#${hintUid}`"
               trigger="manual"
               :enterable="false"
               v-model="delayedHint">
      </tooltip>
    </div>
    <div class="col-xs-7">
      <div class="field-input-container" >
        <div v-if="fieldNote.show" :class="fieldNote.class">
          <i>
            {{ fieldNote.tooltip }}
          </i>
        </div>
        <div v-if="type === 'numeric'">
          <cleave v-model="result"
                  :options="options"
                  ref="input"
                  class="form-control"
                  autocomplete="off"
                  :placeholder="numericPlaceholder"
                  name="numeric">
          </cleave>
        </div>
        <div v-else-if="type === 'date'">
          <cleave v-model="result"
                  ref="input"
                  :options="options"
                  class="form-control"
                  :raw="false"
                  placeholder="jj/mm/aaaa"
                  autocomplete="off"
                  name="date">
          </cleave>
        </div>
        <div v-else-if="type === 'text'">
          <div v-if="parameters.context.multiline">
            <div class="grow-wrap" :data-replicated-value="result">
              <textarea ref="input"
                        class="form-control"
                        :placeholder="parameters.context.placeholder"
                        v-model="result"
                        name="text">
              </textarea>
            </div>
            <btn @click="commit()"
                :disabled="!enableCommit"
                type="primary"
                v-tooltip.unenterable="`<strong>Confirme</strong> le texte et passe au champ suivant<br><span class='key'>Ctrl+Entrée</span>`">
              <strong>Confirmer</strong>
            </btn>
          </div>
           <div v-else>
             <input ref="input"
                    class="form-control"
                    :placeholder="parameters.context.placeholder"
                    v-model="result"
                    name="text">
           </div>
        </div>
        <div v-else-if="type === 'list'">
          <input id="input" class="form-control" type="text"
                 v-model="text" autocomplete="off"
                 ref="input" @keydown.down.prevent
                 @keydown.up.prevent
                 :placeholder="parameters.context.placeholder">
          <typeahead v-model="result" ref="type" open-on-focus
                     open-on-empty :limit="11"
                     force-select
                     :match-start="parameters.context.matchStart"
                     target="#input"
                     :data="listValues"
                     item-key="value">
            <template slot="item" slot-scope="props">
              <li v-for="(item, index) in props.items"
                  :class="{active: props.activeIndex === index}"
                  :key="`item-${index}`">
                <a v-if="index < 10" role="button" @click="props.select(item)">
                  <span v-html="props.highlight(item)"></span>
                </a>
                <span v-else class="text-right more-results">
                  <small><i>Continuer à taper pour plus de résultats...</i></small>
                </span>
              </li>
            </template>
          </typeahead>
        </div>
      </div>
    </div>
    <span v-if="enableCommit"
          v-shortkey.once.prevent="field.shortkey"
          @shortkey="commit()" />
  </div>
  <div v-else
       class="row field-select"
       @click.prevent="select">
    <div class="col-xs-4">
      <div class="field-title-offset">
        <div :class="indentationLevel">
          <span v-html="parameters.context.title"></span>
        </div>
      </div>
    </div>
    <div class="col-xs-1 text-right">
      <span v-if="parameters.readOnly">
        <tooltip :text="tooltips.readOnly" :enterable="false">
          <span class="field-status-icon" data-role="trigger">
              <span class="text-primary glyphicon glyphicon-lock"
                    aria-hidden="true">
              </span>
          </span>
        </tooltip>
      </span>
      <span v-else>
        <tooltip :text="tooltips.edit" :enterable="false">
          <span class="field-status-icon" id="icon-pencil">
            <span class="text-primary glyphicon glyphicon-pencil"
                  aria-hidden="true">
            </span>
          </span>
        </tooltip>
      </span>
    </div>
    <div class="col-xs-6">
        <tooltip :text="impl.tooltip(note.tooltip)" :enterable="false">
          <span :class="{ 'text-danger': note.type === 'error',
                'text-warning' : note.type === 'warning' }"
              id="note">
            <span v-if="display" v-html="display">
            </span>
            <span v-else
                  class=""
                  aria-hidden="true">
              <span class="field-no-value"></span>
            </span>
            <span v-if="note.type === 'warning'"
                  class="glyphicon glyphicon-alert field-glyphicon field-mini-icon">
            </span>
            <span v-else-if="note.type === 'error'"
                  class="glyphicon glyphicon-alert field-glyphicon"
                  aria-hidden="true"></span>
          </span>
        </tooltip>
    </div>
  </div>
</div>
</template>
<script>
import config from '@/sui/components/sheet/add/impl/fieldConfig.js'
import noteImpl from '@/sui/components/sheet/add/impl/notes'

import assert from '@/sui/tools/assert'

import cleave from 'vue-cleave-component'
import debounce from 'debounce'

const impl = {
  tooltip: function (t) {
    if (t == null) {
      return ''
    }

    return `<span class="hint-tooltip">${t}</span>`
  }
}

export default {
  name: 'sheet-add-field',

  props: {
    selected: Boolean,

    value: {
      required: true
    },

    note: {
      type: Object,
      required: true
    },

    parameters: {
      type: Object,
      required: true
    },

    type: {
      type: String,
      required: true
    }
  },

  data: function () {
    const rott = this.parameters.context.tooltip
    return {
      result: null,
      force: false,
      text: '',
      config: config(this),
      hint: this.parameters.hint,
      delayedHint: false,

      impl: impl,
      tooltips: {
        readOnly: impl.tooltip(
          rott || `Lecture seule, valeur remplie automatiquement`
        ),
        edit: impl.tooltip('Modifier')
      }
    }
  },

  computed: {
    hintUid: function () {
      return `sheet-field-hint-${this._uid}`
    },

    hintTooltip: function () {
      return `
<div class="hint-tooltip text-justify">
  ${this.parameters.context.tooltip}
</div>`
    },

    options: function () {
      if (this.field == null) {
        return {}
      }

      return this.field.options()
    },

    indentationLevel: function () {
      assert(this.parameters.indentation != null)

      const result = {}
      result[`indentation-${this.parameters.indentation}`] = true
      return result
    },

    listValues: function () {
      if (this.type !== 'list') {
        return null
      }

      const { values, optional, optionalValue } = this.parameters.context
      if (optional == null || !optional) {
        return values
      }

      const result = [
        {
          code: null,
          value: optionalValue || '(Champ optionnel : ignorer)'
        }
      ]
      result.push(...values)
      return result
    },

    field: function () {
      return this.config[this.type]
    },

    fieldNote: function () {
      return noteImpl.getNote(this.note)
    },

    confirm: function () {
      if (this.field == null) {
        return null
      }

      return debounce(this.commit, this.field.wait)
    },

    display: function () {
      if (this.field == null) {
        return ''
      }

      return this.field.display(this.result)
    },

    valid: function () {
      if (this.field == null) {
        return false
      }

      return this.field.valid(this.result)
    },

    enableCommit: function () {
      return this.valid
    },

    numericPlaceholder: function () {
      if (this.field == null) {
        return ''
      }

      assert(this.type === 'numeric')
      const value = Array(this.parameters.context.digits).fill('x').join('')
      return this.parameters.context.unit
        ? `${value} ${this.parameters.context.unit}` : value
    }
  },

  watch: {
    listValues: function () {
      this.forceResult(this.value)
    },

    value: function (v) {
      this.forceResult(v)
    },

    selected: function (v) {
      if (v) {
        this.selectInput()
        this.delayHint()
      } else {
        this.delayedHint = false
        this.forceResult(this.value)
        this.confirm.clear()
        if (this.type === 'list' && this.result == null) {
          this.text = ''
        }
      }
    },

    text: function (value, old) {
      assert(this.type === 'list')
      if (value !== '' && value !== old &&
          (old == null || !old.startsWith(value))) {
        setTimeout(
          () => this.field.checkSelection(),
          30
        )
      }
    },

    result: function (value, old) {
      if (value == null && old == null) {
        return
      }

      if (this.type === 'list') {
        if (value != null) {
          this.$set(this, 'text', value.value)
        } else if (old != null && this.text === old.value) {
          this.$set(this, 'text', '')
        }
      }

      if (this.selected) {
        if (this.valid && this.parameters.autoNext &&
            (this.field.confirm == null || this.field.confirm())) {
          this.confirm()
        } else {
          this.confirm.clear()
        }
      }
    },

    'parameters.hint': function (v) {
      if (v) {
        this.showHint()
      } else {
        this.hideHint()
      }
    }
  },

  methods: {
    delayHint: function () {
      const vm = this
      setTimeout(() => { vm.delayedHint = vm.hint }, 50)
    },

    showHint: function () {
      this.hint = true
      this.delayHint()
    },

    hideHint: function () {
      this.hint = this.parameters.hint
      this.delayHint()
    },

    forceResult: function (v) {
      this.result = this.field.value(v)
    },

    selectInput: function () {
      const vm = this
      setTimeout(
        function () {
          if (vm.selected) {
            vm.field.select()
          }
        }, 10
      )
    },

    select: function () {
      this.$emit('select')
    },

    commit: function () {
      if (!this.selected || !this.enableCommit) {
        return
      }

      const { valid, value } = this.field.result(this.result)
      if (valid) {
        this.$emit('commit', value)
      }
    }
  },

  mounted: function () {
    this.forceResult(this.value)
    if (this.selected) {
      this.selectInput()
    }
    this.delayHint()
  },

  components: {
    cleave
  }
}
</script>
<style scoped lang="less">
@import '~variables';

@iterations: 5;
.indent-loop (@i) when (@i > 0) {
    @margin: 0 + (@i - 1) * 1;
    @padding: 0.8;
    .indentation-@{i} {
        margin-left: ~"@{margin}rem";
        padding-left: ~"@{padding}rem";
    }
    .indent-loop(@i - 1);
}
.indent-loop (@iterations);

.field-title-offset {
    margin-left: 0.7rem;
}

.field-input-container {
    height: 100%;
    margin-top: 0.3rem;
    margin-bottom: 0.2rem;
    input {
        height: 100%;
    }
}

.field-status-icon {
    border: 0px;
    margin: 0px;
    padding: 0px;
}

.field-mini-icon {
    font-size: 60%;
}

.field-glyphicon {
  margin-left: 0.8rem;
}

.field-no-value {
    padding-left: 3rem;
    border-bottom: 1px solid ;
}

.more-results {
  display: block;
  width: 100%;
  padding: 3px 20px;
  user-select: none;
}

.grow-wrap {
  display: grid;
  margin-bottom: 0.5rem;
}

.grow-wrap::after {
  content: attr(data-replicated-value) " ";
  white-space: pre-wrap;
  visibility: hidden;
}
.grow-wrap > textarea {
  resize: none;
  overflow: hidden;
}
</style>

<style scoped>
.grow-wrap > textarea,
.grow-wrap::after {
  grid-area: 1 / 1 / 2 / 2;
}

.grow-wrap::after {
  padding: 0.5rem;
}
</style>
